import {
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
} from 'reactstrap';

/**
 * Typed Single-Setting Form-Row.
 *
 * Unterstützte Typen:
 *  - 'string' / 'text' (multiline) / 'url'
 *  - 'number'
 *  - 'boolean' (Bootstrap-Switch)
 *  - 'enum' mit `enumValues: string[]`
 *  - 'secret' (never round-trips — Input nur für Update)
 *
 * Props:
 *  - setting: { key, type, value, dbOverridden, envDefault, description?, enumValues?, unit?, isSecret? }
 *  - value: aktuell ge'binte'r Form-Wert
 *  - onChange(newValue)
 *  - onResetToEnvDefault(): triggert DELETE des DB-Overrides
 *  - dirty: ob der Form-Wert vom Server-Wert abweicht
 *  - disabled: während Save laufend
 */
export function SettingsField({
  setting,
  value,
  onChange,
  onResetToEnvDefault,
  dirty,
  disabled,
}) {
  const isSecret = setting.type === 'secret' || setting.isSecret;
  const showSecretInfo = isSecret && !dirty;
  const showEnvIndicator = !dirty && !setting.dbOverridden && !isSecret;
  const showOverrideIndicator = !dirty && setting.dbOverridden && !isSecret;

  return (
    <FormGroup className="mb-3 pb-2 border-bottom">
      <div className="d-flex justify-content-between align-items-start mb-1">
        <Label className="mb-0">
          <code className="small">{setting.key}</code>
          {setting.unit && <span className="text-muted small ms-2">({setting.unit})</span>}
        </Label>
        <div className="d-flex gap-2 align-items-center small">
          {showEnvIndicator && (
            <span className="badge bg-secondary-subtle text-secondary">env-Default</span>
          )}
          {showOverrideIndicator && (
            <span className="badge bg-info-subtle text-info">DB-Override aktiv</span>
          )}
          {dirty && <span className="badge bg-warning-subtle text-warning">geändert</span>}
          {setting.dbOverridden && onResetToEnvDefault && (
            <Button
              color="link"
              size="sm"
              className="p-0 text-decoration-none small"
              onClick={onResetToEnvDefault}
              disabled={disabled}
              title="DELETE des DB-Overrides — server fällt auf env-Default zurück"
            >
              ↺ Reset
            </Button>
          )}
        </div>
      </div>

      {setting.description && (
        <div className="text-muted small mb-2">{setting.description}</div>
      )}

      <FieldInput
        setting={setting}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />

      {showSecretInfo && (
        <div className="text-muted small mt-1">
          {setting.value
            ? `Konfiguriert (••••${String(setting.value).slice(-4)})`
            : 'Nicht gesetzt'}
        </div>
      )}

      {showEnvIndicator && setting.envDefault != null && (
        <div className="text-muted small mt-1">
          env-Default: <code>{String(setting.envDefault)}</code>
        </div>
      )}
    </FormGroup>
  );
}

function FieldInput({ setting, value, onChange, disabled }) {
  if (setting.type === 'boolean') {
    return (
      <div className="form-check form-switch">
        <Input
          type="switch"
          role="switch"
          id={`switch-${setting.key}`}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <Label check for={`switch-${setting.key}`} className="ms-2">
          {value ? 'aktiv' : 'inaktiv'}
        </Label>
      </div>
    );
  }

  if (setting.type === 'enum') {
    return (
      <Input
        type="select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {(setting.enumValues ?? []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Input>
    );
  }

  if (setting.type === 'text') {
    return (
      <Input
        type="textarea"
        rows={4}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  if (setting.type === 'number') {
    return (
      <InputGroup>
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(null);
              return;
            }
            const n = Number(raw);
            onChange(Number.isNaN(n) ? raw : n);
          }}
          disabled={disabled}
        />
        {setting.unit && <InputGroupText>{setting.unit}</InputGroupText>}
      </InputGroup>
    );
  }

  if (setting.type === 'secret') {
    return (
      <Input
        type="password"
        value={value ?? ''}
        placeholder="Neuen Wert eingeben (überschreibt bestehenden)"
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoComplete="new-password"
      />
    );
  }

  // 'string', 'url', default
  return (
    <Input
      type={setting.type === 'url' ? 'url' : 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
}
